import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, Slider, Switch, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material'
import UndoIcon from '@mui/icons-material/Undo'
import useIsMobile from '../../hooks/useIsMobile'
import { imageUrl } from './reviewModel'
import { getColorZones, previewColor } from './photoReviewApi'
import { tintFor, loadImage, renderLayer, drawSegment, maskDataUrl, createLayer, ERASE } from './brushCanvas'

const MIN_SCALE = 1
const MAX_SCALE = 8
const IDENTITY = { scale: 1, x: 0, y: 0 }
const SIZE = { min: 4, max: 96, step: 2, initial: 24 }
const SIZE_PREVIEW_MS = 1200

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

function zoomAbout(view, factor, px, py) {
    const scale = clamp(view.scale * factor, MIN_SCALE, MAX_SCALE)
    if (scale === MIN_SCALE) return IDENTITY
    const ratio = scale / view.scale
    return { scale, x: px - (px - view.x) * ratio, y: py - (py - view.y) * ratio }
}

const CHECKER = {
    backgroundColor: '#fff',
    backgroundImage: 'linear-gradient(45deg,#ddd 25%,transparent 25%),linear-gradient(-45deg,#ddd 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ddd 75%),linear-gradient(-45deg,transparent 75%,#ddd 75%)',
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0,0 8px,8px -8px,-8px 0',
}
const LAYER = { position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }

// White with a dark edge, so it shows on gold, on white metal and on the backdrop.
function SizeRing({ testId, diameter, x, y }) {
    return (
        <Box data-testid={testId} aria-hidden style={{ width: `${diameter}px`, height: `${diameter}px`, left: x, top: y }}
            sx={{
                position: 'absolute', transform: 'translate(-50%, -50%)', borderRadius: '50%', pointerEvents: 'none',
                border: '1.5px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(0,0,0,0.6)',
            }}
        />
    )
}

// Fix color: the automatic zones open as paint, and the reviewer corrects
// them ("this is White", or erased: no metal), previews the match, and
// applies it as a new version of the shot, which still needs Approve.
// Strokes are kept in image pixels (the zones' size), so zoom never changes
// what a stroke covers. Only the corrections are sent (offscreen layers per
// colour plus ERASE): the server keeps its full-resolution automatic zones
// everywhere else.
export default function ColorBrush({ open, slot, label, fetchResource, onApply, onClose }) {
    const { t } = useTranslation()
    const isMobile = useIsMobile()
    const [data, setData] = useState(null)
    const [loadError, setLoadError] = useState(null)
    const [images, setImages] = useState(null)
    const [colour, setColour] = useState(null)
    const [tool, setTool] = useState('paint')
    const [size, setSize] = useState(SIZE.initial)
    const [mode, setMode] = useState('paint')
    const [showPaint, setShowPaint] = useState(true)
    const [hover, setHover] = useState(null)
    const [sizePreview, setSizePreview] = useState(null)
    const [strokes, setStrokes] = useState([])
    const [redraw, setRedraw] = useState(0)
    const [preview, setPreview] = useState(null)
    const [showPreview, setShowPreview] = useState(false)
    const [previewing, setPreviewing] = useState(false)
    const [previewError, setPreviewError] = useState(null)
    const [view, setView] = useState(IDENTITY)

    const stageRef = useRef(null)
    const layerRefs = useRef({})
    const corrections = useRef({})
    const sizeTimer = useRef(null)
    const stroke = useRef(null)
    const pointers = useRef(new Map())
    const gesture = useRef(null)

    const colours = data?.zones.map(zone => zone.colour) ?? []

    useEffect(() => {
        if (!open) return undefined
        let cancelled = false
        setData(null)
        setImages(null)
        setLoadError(null)
        setStrokes([])
        setPreview(null)
        setShowPreview(false)
        setView(IDENTITY)
        getColorZones(fetchResource, slot.photoId).then(async response => {
            if (cancelled) return
            if (response.error) { setLoadError(response.error); return }
            setData(response)
            setColour(response.zones[0]?.colour ?? null)
            const zones = {}
            for (const zone of response.zones) zones[zone.colour] = await loadImage(zone.png)
            const initial = []
            for (const mask of response.masks) initial.push({ colour: mask.colour, image: await loadImage(mask.png) })
            corrections.current = Object.fromEntries([...response.zones.map(zone => zone.colour), ERASE]
                .map(name => [name, createLayer(response.width, response.height)]))
            if (!cancelled) setImages({ zones, initial })
        }).catch(() => { if (!cancelled) setLoadError(t('photoReview.colorBrush.loadFailed')) })
        return () => { cancelled = true }
    }, [open, slot.photoId, fetchResource, t])

    useEffect(() => {
        if (!images) return
        for (const name of Object.keys(layerRefs.current)) {
            const canvas = layerRefs.current[name]
            if (canvas) renderLayer(canvas, { colour: name, base: images.zones[name] ?? null, initial: images.initial, strokes })
        }
        for (const [name, canvas] of Object.entries(corrections.current)) {
            renderLayer(canvas, { colour: name, initial: images.initial, strokes })
        }
    }, [images, strokes, redraw])

    useEffect(() => () => clearTimeout(sizeTimer.current), [])

    const shownScale = () => (stageRef.current.getBoundingClientRect().width / data.width) * view.scale

    function changeSize(value) {
        setSize(value)
        setSizePreview(value * shownScale())
        clearTimeout(sizeTimer.current)
        sizeTimer.current = setTimeout(() => setSizePreview(null), SIZE_PREVIEW_MS)
    }

    function trackHover(e) {
        if (e.pointerType === 'touch' || mode !== 'paint' || showPreview || !data) { setHover(null); return }
        const rect = stageRef.current.getBoundingClientRect()
        setHover({ x: e.clientX - rect.left, y: e.clientY - rect.top, diameter: size * shownScale() })
    }

    const handleWheel = useCallback(e => {
        e.preventDefault()
        const rect = stageRef.current.getBoundingClientRect()
        setView(prev => zoomAbout(prev, Math.exp(-e.deltaY * 0.002), e.clientX - rect.left, e.clientY - rect.top))
    }, [])
    const stageCallback = useCallback(node => {
        if (stageRef.current) stageRef.current.removeEventListener('wheel', handleWheel)
        stageRef.current = node
        if (node) node.addEventListener('wheel', handleWheel, { passive: false })
    }, [handleWheel])

    function imagePoint(e) {
        const rect = stageRef.current.getBoundingClientRect()
        return {
            x: ((e.clientX - rect.left - view.x) / view.scale) * (data.width / rect.width),
            y: ((e.clientY - rect.top - view.y) / view.scale) * (data.height / rect.height),
        }
    }

    function paintTo(point) {
        const current = stroke.current
        const from = current.points.at(-1) ?? null
        current.points.push(point)
        for (const name of colours) {
            const canvas = layerRefs.current[name]
            if (canvas) drawSegment(canvas, name, current, from, point)
        }
    }

    function cancelStroke() {
        if (!stroke.current) return
        stroke.current = null
        setRedraw(n => n + 1)
    }

    function handlePointerDown(e) {
        if (!images) return
        e.currentTarget.setPointerCapture?.(e.pointerId)
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        if (pointers.current.size === 2) {
            cancelStroke()
            const [a, b] = [...pointers.current.values()]
            gesture.current = { kind: 'pinch', distance: Math.hypot(a.x - b.x, a.y - b.y) || 1 }
            return
        }
        if (pointers.current.size > 2) return
        if (mode === 'paint' && !showPreview) {
            stroke.current = { tool, colour: tool === 'paint' ? colour : null, size, points: [] }
            paintTo(imagePoint(e))
        } else {
            gesture.current = { kind: 'pan', x: e.clientX, y: e.clientY }
        }
    }

    function handlePointerMove(e) {
        trackHover(e)
        if (!pointers.current.has(e.pointerId)) return
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
        if (gesture.current?.kind === 'pinch' && pointers.current.size === 2) {
            const [a, b] = [...pointers.current.values()]
            const distance = Math.hypot(a.x - b.x, a.y - b.y) || 1
            const rect = stageRef.current.getBoundingClientRect()
            const factor = distance / gesture.current.distance
            gesture.current.distance = distance
            setView(prev => zoomAbout(prev, factor, (a.x + b.x) / 2 - rect.left, (a.y + b.y) / 2 - rect.top))
            return
        }
        if (stroke.current) {
            paintTo(imagePoint(e))
            return
        }
        if (gesture.current?.kind === 'pan') {
            const dx = e.clientX - gesture.current.x
            const dy = e.clientY - gesture.current.y
            gesture.current = { kind: 'pan', x: e.clientX, y: e.clientY }
            setView(prev => (prev.scale === 1 ? prev : { ...prev, x: prev.x + dx, y: prev.y + dy }))
        }
    }

    function handlePointerUp(e) {
        pointers.current.delete(e.pointerId)
        if (stroke.current) {
            const done = stroke.current
            stroke.current = null
            setStrokes(prev => [...prev, done])
            setPreview(null)
            setShowPreview(false)
        }
        if (pointers.current.size === 0) gesture.current = null
    }

    function paintedMasks() {
        const touched = name => images?.initial.some(mask => mask.colour === name)
            || strokes.some(s => (name === ERASE ? s.tool === 'erase' : s.tool === 'paint' && s.colour === name))
        return [...colours, ERASE].filter(touched).map(name => ({ colour: name, png: maskDataUrl(corrections.current[name]) }))
    }

    async function handlePreview() {
        setPreviewing(true)
        setPreviewError(null)
        try {
            const response = await previewColor(fetchResource, slot.photoId, paintedMasks())
            if (response.error) setPreviewError(response.error)
            else { setPreview(response); setShowPreview(true) }
        } catch {
            setPreviewError(t('photoReview.colorBrush.previewFailed'))
        } finally {
            setPreviewing(false)
        }
    }

    const transform = `translate(${view.x}px, ${view.y}px) scale(${view.scale})`
    const loaded = Boolean(data && images)

    return (
        <Dialog open={Boolean(open)} onClose={onClose} fullScreen={isMobile} fullWidth maxWidth='lg'>
            <DialogTitle sx={{ fontWeight: 600 }}>{t('photoReview.colorBrush.title', { label })}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, overflowY: 'auto' }}>
                {loadError && <Alert severity='error'>{loadError}</Alert>}
                {!loadError && !loaded && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
                {!loadError && data && (
                    <>
                        <Typography variant='body2' color='text.secondary'>{t('photoReview.colorBrush.hint')}</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                            <ToggleButtonGroup size='small' exclusive value={tool === 'erase' ? 'erase' : colour}
                                onChange={(_, value) => {
                                    if (value === null) return
                                    if (value === 'erase') setTool('erase')
                                    else { setTool('paint'); setColour(value) }
                                }}>
                                {colours.map(name => (
                                    <ToggleButton key={name} value={name} aria-label={t(`photoReview.colorBrush.colours.${name}`, name)} sx={{ gap: 0.75 }}>
                                        <Box component='span' sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: tintFor(name) }} />
                                        {t(`photoReview.colorBrush.colours.${name}`, name)}
                                    </ToggleButton>
                                ))}
                                <ToggleButton value='erase'>{t('photoReview.colorBrush.eraser')}</ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 180, flex: '1 1 180px', maxWidth: 280 }}>
                                <Typography variant='body2' sx={{ whiteSpace: 'nowrap' }}>{t('photoReview.colorBrush.size')}</Typography>
                                <Slider size='small' value={size} min={SIZE.min} max={SIZE.max} step={SIZE.step}
                                    onChange={(_, value) => changeSize(value)} getAriaLabel={() => t('photoReview.colorBrush.size')} />
                            </Box>
                            <Button size='small' startIcon={<UndoIcon />} disabled={strokes.length === 0}
                                onClick={() => { setStrokes(prev => prev.slice(0, -1)); setPreview(null); setShowPreview(false) }}>
                                {t('photoReview.colorBrush.undo')}
                            </Button>
                            <ToggleButtonGroup size='small' exclusive value={mode} onChange={(_, value) => value && setMode(value)}>
                                <ToggleButton value='paint'>{t('photoReview.colorBrush.paint')}</ToggleButton>
                                <ToggleButton value='move'>{t('photoReview.colorBrush.move')}</ToggleButton>
                            </ToggleButtonGroup>
                            <FormControlLabel
                                control={<Switch size='small' checked={showPaint} onChange={e => setShowPaint(e.target.checked)} />}
                                label={t('photoReview.colorBrush.showPaint')}
                            />
                        </Box>
                        {preview && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                                <ToggleButtonGroup size='small' exclusive value={showPreview ? 'result' : 'painting'}
                                    onChange={(_, value) => value && setShowPreview(value === 'result')}>
                                    <ToggleButton value='painting'>{t('photoReview.colorBrush.painting')}</ToggleButton>
                                    <ToggleButton value='result'>{t('photoReview.colorBrush.result')}</ToggleButton>
                                </ToggleButtonGroup>
                                {preview.flagged && (
                                    <Alert severity='warning' sx={{ flex: '1 1 260px' }}>
                                        {t('photoReview.colorBrush.flagged')}
                                        {preview.reasons.map(reason => <div key={reason}>{reason}</div>)}
                                    </Alert>
                                )}
                            </Box>
                        )}
                        {previewError && <Alert severity='error' onClose={() => setPreviewError(null)}>{previewError}</Alert>}
                        <Box
                            ref={stageCallback}
                            data-testid='brush-stage'
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            onPointerCancel={handlePointerUp}
                            onPointerLeave={() => setHover(null)}
                            sx={{
                                ...CHECKER, position: 'relative', overflow: 'hidden', touchAction: 'none', userSelect: 'none', flexShrink: 0,
                                width: '100%', maxWidth: `calc(65svh * ${data.width / data.height})`, mx: 'auto',
                                aspectRatio: `${data.width} / ${data.height}`, borderRadius: 1,
                                cursor: mode === 'move' || showPreview ? 'grab' : 'none',
                            }}
                        >
                            <Box sx={{ position: 'absolute', inset: 0, transform, transformOrigin: '0 0' }}>
                                <img src={imageUrl(slot.photoId, 'edited', slot.attempts, 'preview')} alt={t('photoReview.colorBrush.editedAlt', { label })}
                                    draggable={false} style={{ ...LAYER, objectFit: 'contain' }} />
                                {colours.map(name => (
                                    <canvas key={name} ref={node => { layerRefs.current[name] = node }} width={data.width} height={data.height}
                                        style={{ ...LAYER, opacity: showPaint ? 0.5 : 0 }} />
                                ))}
                                {/* Over the painting rather than instead of it: the layers must stay mounted to keep their strokes. */}
                                {showPreview && preview && (
                                    <Box sx={{ ...CHECKER, ...LAYER }}>
                                        <img src={preview.image} alt={t('photoReview.colorBrush.previewAlt', { label })} draggable={false} style={{ ...LAYER, objectFit: 'contain' }} />
                                    </Box>
                                )}
                            </Box>
                            {hover && <SizeRing testId='brush-cursor' diameter={hover.diameter} x={hover.x} y={hover.y} />}
                            {sizePreview !== null && <SizeRing testId='brush-size-preview' diameter={sizePreview} x='50%' y='50%' />}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 'calc(16px + env(safe-area-inset-bottom))' }}>
                <Button onClick={onClose}>{t('photoReview.colorBrush.cancel')}</Button>
                {loaded && (
                    <>
                        <Button variant='outlined' disabled={previewing} onClick={handlePreview}>
                            {previewing ? <CircularProgress size={18} /> : t('photoReview.colorBrush.preview')}
                        </Button>
                        <Button variant='contained' disabled={previewing} onClick={() => onApply(paintedMasks())}>{t('photoReview.colorBrush.apply')}</Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    )
}
